import type { GraphModification, ConceptFragment, Graph } from "@lr-notitia/notitia-engineer/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-engineer/client";
import type { Terminology } from "@lr-notitia/notitia-dictionary/schemas";
import { handleGraphModification } from "./utils";
import { useSymbolsStore } from "../symbols";
import * as Metrics from "../metrics";

let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	symbolsStore = useSymbolsStore();
}

export const conceptFragments = (graph: Ref<Graph>) =>
	computed<ConceptFragment[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "fragment" && node.layer === "concept"
			) as ConceptFragment[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});
export function conceptFragment(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => {
			Metrics.recordAccess(id);
			return graph.value.nodes[id] as ConceptFragment | undefined;
		},
		add: (data: { refersTo: string; localTerminology: Terminology }) => add(domainId, graph, data),
		update: (fragment: ConceptFragment) => update(domainId, graph, fragment),
		remove: (id: string) => remove(domainId, graph, id),
	};
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	data: { refersTo: string; localTerminology: Terminology }
): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		api()
			.domain(domainId.value)
			.fragments()
			.post(data)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, fragment: ConceptFragment): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[fragment.id]) return reject();
		api()
			.domain(domainId.value)
			.fragment(fragment.id)
			.put(fragment)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.fragment(id)
			.del()
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
